/**
 * Hand-rolled Ory Kratos self-service flow client.
 *
 * Documented low-level `fetch` exception (see AGENTS.md): Kratos flows
 * are NOT Tktic JSON API endpoints — they live behind the same-origin
 * `/kratos/*` proxy (Caddy in prod, Vite in dev), authenticate with
 * Kratos's own CSRF + `ory_kratos_session` cookies, and must never
 * carry Tktic bearer/CSRF headers. Every request sends
 * `Accept: application/json` so Kratos answers flow JSON instead of
 * browser redirects, plus `credentials: "include"` for the first-party
 * cookies.
 *
 * Contract notes (mirrors Kratos v1.x browser-flow semantics):
 * - 200 with a `ui` body is an updated flow (settings success,
 *   verification "sent email" state, ...); 200 without `ui` is a
 *   terminal success (login/registration return `session` +
 *   `continue_with`).
 * - 400 with a `ui` body is the same flow re-rendered with per-node
 *   validation messages.
 * - 410 means the flow expired — callers re-create transparently
 *   (`useKratosFlow` resubmits once against a fresh flow).
 * - 422 `browser_location_change_required` either points at another
 *   Kratos flow (`?flow=` fetched as JSON) or an external provider
 *   redirect that the browser must follow.
 */

export type KratosUiText = {
  id: number;
  text: string;
  type: "info" | "error" | "success" | string;
  context?: Record<string, unknown>;
};

export type KratosUiNodeAttributes = {
  node_type: string;
  name?: string;
  type?: string;
  value?: unknown;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  [key: string]: unknown;
};

export type KratosUiNode = {
  type: "input" | "script" | "text" | "img" | "a" | string;
  group: string;
  attributes: KratosUiNodeAttributes;
  messages: KratosUiText[];
  meta?: { label?: KratosUiText };
};

export type KratosFlow = {
  id: string;
  type?: string;
  state?: string;
  expires_at?: string;
  requested_aal?: string;
  ui: {
    action: string;
    method: string;
    nodes: KratosUiNode[];
    messages?: KratosUiText[];
  };
};

export type KratosContinueWith = {
  action: string;
  flow?: { id: string; url?: string; verifiable_address?: string };
};

export type KratosSuccessPayload = {
  session?: Record<string, unknown>;
  session_token?: string;
  continue_with?: KratosContinueWith[];
  identity?: Record<string, unknown>;
};

export type FlowKind =
  | "login"
  | "registration"
  | "verification"
  | "recovery"
  | "settings";

export type SubmitOutcome =
  /** Terminal success — login/registration completed. */
  | { kind: "success"; data: KratosSuccessPayload }
  /** The same or a follow-up flow to keep rendering (validation
   * errors, "enter the code we sent", settings saved, ...). */
  | { kind: "flow"; flow: KratosFlow; flowKind: FlowKind };

export class KratosError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? kratosErrorMessage(body) ?? `Kratos HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

export class KratosFlowExpiredError extends KratosError {}

export class KratosBrowserRedirectError extends KratosError {
  readonly redirectTo: string;

  constructor(status: number, body: unknown, redirectTo: string) {
    super(status, body, "Continue in your identity provider.");
    this.redirectTo = redirectTo;
  }
}

/** Settings flows demand a recently-authenticated Kratos session;
 * Kratos answers 401/403 `session_refresh_required`. The caller
 * prompts an inline re-auth (login flow with `refresh=true`). */
export class KratosSessionRefreshError extends KratosError {}

function kratosErrorMessage(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const error = (body as { error?: { message?: string; reason?: string } }).error;
  return error?.reason || error?.message;
}

export function kratosErrorId(body: unknown): string {
  if (!body || typeof body !== "object") return "";
  const error = (body as { error?: { id?: string } }).error;
  return error?.id ?? "";
}

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isFlowBody(body: unknown): body is KratosFlow {
  return !!body && typeof body === "object" && "ui" in body && "id" in body;
}

const KRATOS_HEADERS = { Accept: "application/json" };

async function kratosFetch(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, {
    credentials: "include",
    ...init,
    headers: { ...KRATOS_HEADERS, ...(init?.headers ?? {}) },
  });
}

function throwForError(status: number, body: unknown): never {
  if (status === 410) throw new KratosFlowExpiredError(status, body);
  const id = kratosErrorId(body);
  if (id === "self_service_flow_expired") {
    throw new KratosFlowExpiredError(status, body);
  }
  if (id === "session_refresh_required" || id === "session_aal2_required") {
    throw new KratosSessionRefreshError(status, body);
  }
  throw new KratosError(status, body);
}

/** Start a fresh browser flow as JSON. `params` maps onto Kratos query
 * params (e.g. `{ refresh: "true" }` for settings re-auth). */
export async function createFlow(
  kind: FlowKind,
  params?: Record<string, string>,
): Promise<KratosFlow> {
  const query = new URLSearchParams(params ?? {}).toString();
  const res = await kratosFetch(
    `/kratos/self-service/${kind}/browser${query ? `?${query}` : ""}`,
  );
  const body = await parseJson(res);
  if (res.ok && isFlowBody(body)) return body;
  throwForError(res.status, body);
}

export function currentBrowserReturnTo(): string {
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}`;
}

/** Fetch an existing flow by id (deep links: `/verify-email?flow=`,
 * post-registration `continue_with`, 422 follow-ups). */
export async function getFlow(kind: FlowKind, id: string): Promise<KratosFlow> {
  const res = await kratosFetch(
    `/kratos/self-service/${kind}/flows?id=${encodeURIComponent(id)}`,
  );
  const body = await parseJson(res);
  if (res.ok && isFlowBody(body)) return body;
  // A 404/403 on flow lookup behaves like expiry for the caller: the
  // only recovery is a fresh flow.
  if (res.status === 404 || res.status === 403 || res.status === 410) {
    throw new KratosFlowExpiredError(res.status, body);
  }
  throwForError(res.status, body);
}

function flowKindFromUrl(url: string): FlowKind | null {
  const match = url.match(
    /\/self-service\/(login|registration|verification|recovery|settings)\//,
  );
  return (match?.[1] as FlowKind | undefined) ?? null;
}

function redirectBrowserTo(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const payload = body as {
    redirect_browser_to?: unknown;
    error?: { details?: { redirect_browser_to?: unknown } };
  };
  const redirect =
    payload.redirect_browser_to ?? payload.error?.details?.redirect_browser_to;
  return typeof redirect === "string" ? redirect : undefined;
}

/** Submit a flow body against `flow.ui.action`. The CSRF token from the
 * flow's hidden node is merged automatically. */
export async function submitFlow(
  kind: FlowKind,
  flow: KratosFlow,
  values: Record<string, unknown>,
): Promise<SubmitOutcome> {
  // Keep the request same-origin even if Kratos renders an absolute
  // action URL — strip down to path + query.
  const action = new URL(flow.ui.action, window.location.origin);
  const body: Record<string, unknown> = {
    csrf_token: csrfTokenFromFlow(flow),
    ...values,
  };
  const res = await kratosFetch(`${action.pathname}${action.search}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const parsed = await parseJson(res);

  if (res.ok) {
    if (isFlowBody(parsed)) return { kind: "flow", flow: parsed, flowKind: kind };
    return { kind: "success", data: (parsed ?? {}) as KratosSuccessPayload };
  }
  if (res.status === 400 && isFlowBody(parsed)) {
    return { kind: "flow", flow: parsed, flowKind: kind };
  }
  if (res.status === 422) {
    // browser_location_change_required — follow the flow id, not the URL.
    const redirect = redirectBrowserTo(parsed);
    if (redirect) {
      const url = new URL(redirect, window.location.origin);
      const flowId = url.searchParams.get("flow");
      const nextKind = flowKindFromUrl(redirect) ?? kindFromUiPath(url.pathname);
      if (flowId && nextKind) {
        const next = await getFlow(nextKind, flowId);
        return { kind: "flow", flow: next, flowKind: nextKind };
      }
      throw new KratosBrowserRedirectError(res.status, parsed, redirect);
    }
    throwForError(res.status, parsed);
  }
  throwForError(res.status, parsed);
}

/** Map a Kratos ui_url path back to a flow kind (the 422 redirect may
 * point at our SPA route rather than a /self-service/ URL). */
function kindFromUiPath(pathname: string): FlowKind | null {
  if (pathname === "/login") return "login";
  if (pathname === "/register") return "registration";
  if (pathname === "/verify-email") return "verification";
  if (pathname === "/recovery") return "recovery";
  if (pathname === "/me") return "settings";
  return null;
}

// ---------------------------------------------------------------------------
// Flow inspection helpers
// ---------------------------------------------------------------------------

export function nodeByName(
  flow: KratosFlow,
  name: string,
): KratosUiNode | undefined {
  return flow.ui.nodes.find((node) => node.attributes.name === name);
}

export function csrfTokenFromFlow(flow: KratosFlow): string {
  const value = nodeByName(flow, "csrf_token")?.attributes.value;
  return typeof value === "string" ? value : "";
}

export function hasMethod(flow: KratosFlow, group: string): boolean {
  return flow.ui.nodes.some((node) => node.group === group);
}

export type KratosOidcProvider = {
  id: string;
  label: string;
  disabled: boolean;
};

const PROVIDER_LABELS: Record<string, string> = {
  google: "Google",
  apple: "Apple ID",
};

function providerLabel(id: string, label?: string): string {
  const normalized = id.trim().toLowerCase();
  if (PROVIDER_LABELS[normalized]) return PROVIDER_LABELS[normalized];
  const text = (label || "").trim();
  if (text) {
    return text
      .replace(/^continue with\s+/i, "")
      .replace(/^sign in with\s+/i, "")
      .replace(/^sign up with\s+/i, "")
      .trim();
  }
  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function oidcProviders(flow: KratosFlow): KratosOidcProvider[] {
  return flow.ui.nodes
    .filter(
      (node) =>
        node.group === "oidc" &&
        node.attributes.name === "provider" &&
        node.attributes.type === "submit",
    )
    .map((node) => {
      const id = String(node.attributes.value ?? "").trim();
      return {
        id,
        label: providerLabel(id, node.meta?.label?.text),
        disabled: Boolean(node.attributes.disabled),
      };
    })
    .filter((provider) => provider.id);
}

export async function submitOidcProvider(
  kind: Extract<FlowKind, "login" | "registration">,
  flow: KratosFlow,
  providerId: string,
): Promise<SubmitOutcome | { kind: "redirect"; redirectTo: string }> {
  try {
    return await submitFlow(kind, flow, { method: "oidc", provider: providerId });
  } catch (err) {
    if (err instanceof KratosBrowserRedirectError) {
      window.location.assign(err.redirectTo);
      return { kind: "redirect", redirectTo: err.redirectTo };
    }
    throw err;
  }
}

/** Per-field validation messages (Kratos 400 re-render). */
export function nodeMessages(flow: KratosFlow, name: string): string[] {
  return (nodeByName(flow, name)?.messages ?? []).map((m) => m.text);
}

/** Flow-level messages, errors first. */
export function flowMessages(flow: KratosFlow): KratosUiText[] {
  return [...(flow.ui.messages ?? [])].sort((a, b) =>
    a.type === b.type ? 0 : a.type === "error" ? -1 : 1,
  );
}

export function firstFlowError(flow: KratosFlow): string | null {
  const error =
    flowMessages(flow).find((m) => m.type === "error") ??
    flow.ui.nodes.flatMap((n) => n.messages).find((m) => m.type === "error");
  return error?.text ?? null;
}

/** Human copy for hard client errors. */
export function kratosErrorCopy(err: unknown): string {
  if (err instanceof KratosFlowExpiredError) {
    return "That sign-in attempt expired. Please try again.";
  }
  if (err instanceof KratosSessionRefreshError) {
    return "Please confirm your password again to continue.";
  }
  if (err instanceof KratosBrowserRedirectError) {
    return "Continue in your identity provider.";
  }
  if (err instanceof KratosError) {
    return err.message || "Secure sign-in could not finish. Please try again.";
  }
  return "Network error. Try again.";
}
